import { Link } from 'react-router-dom';
import { MEMBERSHEEP_APP_STORE_URL } from '@/membersheep/config';
import { MS_PAGES, msPath, useMsCopy, useMsLocale, type MsCopy } from '@/membersheep/i18n';
import memberSheepIcon from '@/membersheep/assets/membersheep-512.png';
import sheep from '@/membersheep/assets/membersheep-sheep.png';
import iosBadgeEn from '@/travo/assets/images/shared/ios-badge-en.svg';
import iosBadgeZhTW from '@/travo/assets/images/shared/ios-badge-zh-tw.svg';
import { FAQSchema } from '@/shared/seo';
import MsAppSchema from './MsAppSchema';
import { MsHead } from './MsHead';

const PANEL = 'border border-sys-separator bg-sys-surface';

/** Made-up member cards for the hero stack; they fan out on hover. */
/** The hero stack's cards, top to bottom: each one's look; its name and kind come from the copy. */
const HERO_CARDS: { id: keyof MsCopy['home']['heroCards']; className: string }[] = [
  { id: 'cafe', className: 'top-[110px] bg-[linear-gradient(135deg,#1f2937,#0b0f17)] -rotate-[7deg] -translate-x-6 group-hover:-rotate-[10deg] group-hover:-translate-x-[50px] group-hover:-translate-y-[18px]' },
  { id: 'gym', className: 'top-[180px] bg-[linear-gradient(135deg,#e5484d,#a8242a)] rotate-[4deg] translate-x-[30px] group-hover:rotate-[7deg] group-hover:translate-x-14 group-hover:-translate-y-1.5' },
  { id: 'grocer', className: 'top-[250px] bg-[linear-gradient(135deg,#12a594,#0b6e63)] -rotate-3' },
  { id: 'books', className: 'top-[322px] bg-[linear-gradient(135deg,#f5a524,#c26c08)] rotate-2 -translate-x-2.5 group-hover:rotate-[4deg] group-hover:-translate-x-[26px] group-hover:translate-y-[18px]' }
];

const FEATURE_ICONS = [
  <path key="scan" d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3M7 12h10" />,
  <path key="sun" d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />,
  <path key="lock" d="M7 10h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8 10V7a4 4 0 0 1 8 0v3" />
];

const AppStoreButton = () => {
  const copy = useMsCopy();
  const locale = useMsLocale();

  if (MEMBERSHEEP_APP_STORE_URL) {
    return (
      <a href={MEMBERSHEEP_APP_STORE_URL} className="mt-8 self-start">
        <img src={locale === 'zh-TW' ? iosBadgeZhTW : iosBadgeEn} alt={copy.home.download} height={48} className="h-12 w-auto" />
      </a>
    );
  }
  return (
    <span className="mt-8 inline-flex items-center gap-2.5 self-start rounded-xl border border-white/30 bg-black/20 px-[18px] py-3 text-[15px] font-semibold backdrop-blur-md">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-[18px] w-[18px]">
        <path d="M16.4 12.6c0-2.4 2-3.5 2-3.6-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9s-1.9-.9-3.1-.8C6.8 7.3 5.3 8.2 4.5 9.6c-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.1-.8s1.9.8 3.1.8c1.3 0 2.1-1.2 2.9-2.4.9-1.3 1.3-2.6 1.3-2.7 0 0-2.7-1-2.7-3.9zM14.1 5.5c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1 .1 2.1-.5 2.7-1.3z" />
      </svg>
      {copy.home.comingSoon}
    </span>
  );
};

const Hero = () => {
  const copy = useMsCopy();

  return (
    <section className="group relative mt-4 grid overflow-hidden rounded-3xl bg-[radial-gradient(120%_90%_at_85%_0%,var(--color-ms-blue-light)_0%,var(--color-ms-blue)_38%,var(--color-ms-blue-deep)_100%)] text-white md:min-h-[560px] md:grid-cols-[1.05fr_1fr] md:rounded-[32px]">
      <div className="relative z-10 flex flex-col justify-center px-6 pt-10 pb-2 md:px-14 md:py-16">
        <img src={memberSheepIcon} alt={copy.home.iconAlt} width={72} height={72} className="h-[72px] w-[72px] rounded-[17px] shadow-[0_10px_30px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.25)]" />
        {/* 繁中 sets more glyphs per line than English: smaller, so each line of the title stays whole */}
        <h1 className="mt-7 font-display text-[clamp(40px,5.6vw,68px)] leading-[0.98] font-extrabold tracking-[-0.04em] whitespace-pre-line [&:lang(zh-Hant-TW)]:text-[clamp(36px,4.6vw,58px)] [&:lang(zh-Hant-TW)]:leading-[1.08]">
          {copy.home.title}
        </h1>
        <p className="mt-[18px] max-w-[30ch] text-[19px] text-white/85">{copy.home.body}</p>
        <AppStoreButton />
      </div>
      <div aria-hidden="true" className="relative h-[460px] md:h-auto">
        <img src={sheep} alt="" className="absolute top-[26px] left-[calc(50%+10px)] w-[150px] drop-shadow-[0_6px_12px_rgba(0,0,0,0.2)]" />
        {HERO_CARDS.map((card) => (
          <div
            key={card.id}
            className={`absolute left-1/2 -ml-[130px] flex h-[164px] w-[260px] flex-col rounded-[18px] px-5 py-[18px] shadow-[0_24px_40px_-16px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] motion-safe:transition-transform motion-safe:duration-500 md:-ml-[150px] md:h-[188px] md:w-[300px] ${card.className}`}
          >
            <span className="font-display text-[17px] font-bold">{copy.home.heroCards[card.id].name}</span>
            <span className="mt-1 text-xs opacity-80">{copy.home.heroCards[card.id].kind}</span>
            <span className="barcode mt-auto h-[46px] rounded-lg px-2.5 py-[7px]" />
          </div>
        ))}
      </div>
    </section>
  );
};

/** /membersheep — what the app does, Plus, and the legal pages. */
const MsHomePage = () => {
  const copy = useMsCopy();
  const locale = useMsLocale();
  const legalLinks = [
    { page: MS_PAGES.privacy, title: copy.privacy.title, body: copy.home.links.privacy },
    { page: MS_PAGES.terms, title: copy.terms.title, body: copy.home.links.terms },
    { page: MS_PAGES.support, title: copy.support.title, body: copy.home.links.support }
  ];

  return (
    <main className="mx-auto max-w-[1080px] px-4 md:px-6">
      <MsHead page={MS_PAGES.home} {...copy.seo.home} />
      <MsAppSchema />
      <FAQSchema items={copy.quickAnswers.items} />
      <Hero />

      <div className="mt-[72px] grid gap-px overflow-hidden rounded-3xl border border-sys-separator bg-sys-separator md:grid-cols-3">
        {copy.home.features.map((feature, i) => (
          <div key={feature.title} className="bg-sys-surface px-7 py-[30px]">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-[26px] w-[26px] text-ms-accent dark:text-ms-blue">
              {FEATURE_ICONS[i]}
            </svg>
            <h2 className="mt-[18px] font-display text-xl leading-tight font-bold tracking-[-0.01em]">{feature.title}</h2>
            <p className="mt-2 text-[15px] text-sys-secondary">{feature.body}</p>
          </div>
        ))}
      </div>

      <section className="mt-[72px] grid items-center gap-6 md:grid-cols-2 md:gap-12">
        <div>
          <h2 className="font-display text-[40px] leading-[1.02] font-extrabold tracking-[-0.035em]">
            {copy.home.plusLead} <span className="text-[#e8a917]">{copy.home.plusAccent}</span>
          </h2>
          <p className="mt-3.5 text-sys-secondary">{copy.home.plusBody}</p>
        </div>
        <ul className="grid gap-2.5">
          {copy.home.plusItems.map((item) => (
            <li key={item} className={`flex gap-3 rounded-[14px] px-[18px] py-4 ${PANEL}`}>
              <span aria-hidden="true" className="font-bold text-ms-accent dark:text-ms-blue">
                ✓
              </span>
              {item}
            </li>
          ))}
        </ul>
      </section>

      {/* Plain question-and-answer text that answer engines can quote; mirrored in the FAQPage JSON-LD above */}
      <section aria-labelledby="quick-answers" className="mt-[72px]">
        <h2 id="quick-answers" className="font-display text-[28px] leading-tight font-bold tracking-[-0.02em]">
          {copy.quickAnswers.title}
        </h2>
        <dl className="mt-6 grid gap-3 md:grid-cols-2">
          {copy.quickAnswers.items.map((item) => (
            <div key={item.question} className={`rounded-2xl px-6 py-5 ${PANEL}`}>
              <dt className="font-display text-lg leading-snug font-semibold">{item.question}</dt>
              <dd className="mt-2 text-[15px] text-sys-secondary">{item.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <nav aria-label={copy.nav.primary} className="mt-[72px] grid gap-3 md:grid-cols-3">
        {legalLinks.map((link) => (
          <Link key={link.page} to={msPath(locale, link.page)} className={`rounded-2xl px-6 py-[22px] hover:border-ms-accent ${PANEL}`}>
            <strong className="block font-display text-lg">{link.title}</strong>
            <span className="text-sm text-sys-secondary">{link.body}</span>
          </Link>
        ))}
      </nav>
    </main>
  );
};

export default MsHomePage;
